import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type UIEvent,
} from 'react';

import { createResizeObserverWithRAF } from '@heidi/utils';

const checkHasHorizontalOverflow = (el: HTMLElement) => el.scrollWidth > el.clientWidth;

const checkIsScrolledToLeft = (el: HTMLElement) => el.scrollLeft === 0;

const checkIsScrolledToRight = (el: HTMLElement) =>
  el.scrollWidth - el.scrollLeft <= el.clientWidth + 1;

/**
 * `getComputedStyle` requires a real DOM `Element`; guard so non-Element test doubles (and any
 * SSR pass with no `window`) fall back to the historical LTR assumption. Reading a computed style
 * forces a style recalc, so callers resolve this off the scroll hot path (see the hook) and cache
 * the result — the writing direction is effectively stable for a given element.
 */
const isRtl = (el: HTMLElement): boolean =>
  typeof window !== 'undefined' &&
  typeof Element !== 'undefined' &&
  el instanceof Element &&
  window.getComputedStyle(el).direction === 'rtl';

/**
 * Which *physical* edges have clipped content, correct in both writing directions.
 *
 * The `left`/`right` flags are physical (consumers position their fades with physical `left`/
 * `right`), but `scrollLeft` is measured from the content's inline-*start* edge, so its meaning
 * flips under RTL. In LTR inline-start is the physical-left edge and `scrollLeft` runs 0…maxScroll.
 * In RTL (the negative-`scrollLeft` model every current browser and the desktop WKWebView follow)
 * inline-start is the physical-*right* edge: the initial position is flush-right with
 * `scrollLeft === 0`, and scrolling toward the hidden left content drives `scrollLeft` negative
 * (0…-maxScroll). Reading the LTR checks verbatim in RTL therefore lights the wrong physical edge
 * — the fade appeared on the right at rest when the clipped content was on the left (LOC-522). The
 * RTL branch mirrors the LTR checks across the inline axis so each fade tracks its physical edge;
 * the LTR branch is byte-for-byte unchanged.
 */
const getGradients = (el: HTMLElement, elementIsRtl: boolean) => {
  if (!checkHasHorizontalOverflow(el)) {
    return { left: false, right: false };
  }
  if (elementIsRtl) {
    return {
      // Mirror of `checkIsScrolledToRight`: content hidden past the physical-left edge is
      // `(scrollWidth - clientWidth) + scrollLeft` (scrollLeft ≤ 0), same 1px rounding guard.
      left: el.scrollWidth - el.clientWidth + el.scrollLeft > 1,
      // Mirror of `checkIsScrolledToLeft`: nothing clipped on the physical-right at rest
      // (scrollLeft === 0); any leftward scroll (negative) reveals it.
      right: el.scrollLeft < 0,
    };
  }
  return {
    left: !checkIsScrolledToLeft(el),
    right: !checkIsScrolledToRight(el),
  };
};

interface GradientState {
  showLeftGradient: boolean;
  showRightGradient: boolean;
}

const INITIAL_STATE: GradientState = {
  showLeftGradient: false,
  showRightGradient: false,
};

interface UseHorizontalScrollGradientOptions {
  /** Called on mount and whenever the observed element resizes. */
  onResize?: (el: HTMLElement) => void;
  /**
   * Also recompute the gradients when the observed element's **content** changes (children
   * added/removed, text edited), via a `MutationObserver`.
   *
   * The `ResizeObserver` only fires on the scroller's own box size, so a bounded scroller whose
   * `scrollWidth` changes while its `clientWidth` does not — e.g. tabs added/removed/renamed in
   * a fixed-width strip — would otherwise keep stale fade edges until the next scroll or window
   * resize. Opt-in (default `false`) so consumers that don't have dynamic content don't pay for
   * a `MutationObserver`. @default false
   */
  observeContentChanges?: boolean;
}

/**
 * Hook to manage gradient visibility based on horizontal scroll position.
 * `showLeftGradient` / `showRightGradient` track the *physical* left/right edges and stay correct
 * under RTL (where `scrollLeft` runs 0…-maxScroll from a flush-right start) as well as LTR.
 */
export const useHorizontalScrollGradient = (
  ref: RefObject<HTMLElement | null>,
  options?: UseHorizontalScrollGradientOptions
) => {
  const [gradients, setGradients] = useState<GradientState>(INITIAL_STATE);
  const onResize = options?.onResize;
  const observeContentChanges = options?.observeContentChanges ?? false;

  // Cached writing direction. Resolving it (`getComputedStyle`) forces a style recalc, so we keep
  // it off the scroll hot path: it's refreshed on mount / resize / content mutation (in `update`
  // below) — the occasions the layout can actually change — and reused here on every scroll.
  const isRtlRef = useRef(false);

  // Bail out when gradient visibility is unchanged. This hook can run on
  // shared horizontal/vertical scroll containers, where vertical scroll events
  // should not re-render consumers if the horizontal-gradient booleans are the
  // same.
  const applyGradients = useCallback((el: HTMLElement) => {
    const { left, right } = getGradients(el, isRtlRef.current);
    setGradients((prev) =>
      prev.showLeftGradient === left && prev.showRightGradient === right
        ? prev
        : { showLeftGradient: left, showRightGradient: right }
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      // Refresh the cached direction here (not in `applyGradients`) so the per-scroll path stays
      // free of the `getComputedStyle` style recalc.
      isRtlRef.current = isRtl(el);
      applyGradients(el);
      onResize?.(el);
    };

    update();

    const observer = createResizeObserverWithRAF(update);
    observer.observe(el);

    // The ResizeObserver above only catches the scroller's own box-size changes. When content
    // is added/removed/renamed the box can stay the same size while `scrollWidth` changes, so
    // optionally watch content mutations too and recompute — coalescing bursts into one
    // rAF-batched update, mirroring the ResizeObserver path.
    let mutationObserver: MutationObserver | undefined;
    let rafId: number | undefined;
    if (observeContentChanges && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        if (rafId !== undefined) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(update);
      });
      mutationObserver.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      observer.disconnect();
      mutationObserver?.disconnect();
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, [ref, onResize, applyGradients, observeContentChanges]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      applyGradients(event.currentTarget);
    },
    [applyGradients]
  );

  return { ...gradients, handleScroll };
};
